import { useParams, Link, Navigate } from 'react-router-dom'
import { Calendar, ArrowLeft, GraduationCap, Award, CheckCircle2, Phone, Mail, MapPin } from 'lucide-react'
import { scrapedData } from '../data'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function TherapistDetail() {
  const { slug } = useParams()
  const therapist = scrapedData.therapists.find((t) => t.slug === slug)

  if (!therapist) {
    return <Navigate to="/om-os" replace />
  }

  return (
    <div className="space-y-16 pb-20">
      <SEO
        title={`${therapist.name} | ${therapist.title} | Rørbækklinikken Viborg`}
        description={`${therapist.name} på Rørbækklinikken i Viborg. ${therapist.aboutDescription}`}
      />

      {/* Breadcrumb / Back Navigation */}
      <section className="bg-[#F5F0E6]/50 border-b border-[#E6DFD3] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/om-os"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E352F]/70 hover:text-[#1E352F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbage til Om Rørbækklinikken
          </Link>
        </div>
      </section>

      {/* Profile Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-14 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Portrait Column */}
            <div className="lg:col-span-5 flex flex-col items-center sm:items-start space-y-6">
              <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-2xl overflow-hidden border-4 border-[#F5F0E6] shadow-md bg-[#FDFBF7]">
                <img
                  src={imageUrl(therapist.image, 'width=600,quality=85,format=auto')}
                  alt={therapist.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="w-full bg-[#FDFBF7] rounded-2xl p-6 border border-[#E6DFD3] space-y-4">
                <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                  Direkte kontakt til klinikken
                </h3>
                <div className="space-y-3 text-sm text-[#1E352F]/80">
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#eebb53] shrink-0" />
                    <a href="tel:86639393" className="hover:underline font-semibold">
                      86 63 93 93
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#eebb53] shrink-0" />
                    <a href="mailto:im@muskelterapi.com" className="hover:underline">
                      im@muskelterapi.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-[#eebb53] shrink-0" />
                    <span>Gravene 28B, 8800 Viborg</span>
                  </div>
                </div>

                <div className="pt-2">
                  <a
                    href="https://roerbaekklinikken.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#eebb53] hover:bg-[#dca63d] text-white text-center font-bold py-3 px-6 rounded-full text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    Book tid online
                  </a>
                </div>
              </div>
            </div>

            {/* Profile Info Column */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 rounded-full bg-[#1E352F]/10 text-[#1E352F] text-xs font-semibold uppercase tracking-wider">
                  Behandler på Rørbækklinikken
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
                  {therapist.name}
                </h1>
                <p className="text-base sm:text-lg font-semibold text-[#eebb53]">
                  {therapist.title}
                </p>
              </div>

              {/* Personal Description */}
              <div className="space-y-4 text-base sm:text-lg text-[#1E352F]/85 leading-relaxed bg-[#FDFBF7]/80 p-6 rounded-2xl border border-[#E6DFD3]">
                <p>{therapist.aboutDescription}</p>
              </div>

              {/* Specialer & Behandlingstyper */}
              {therapist.specialties && therapist.specialties.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-serif text-xl font-bold text-[#1E352F] flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#eebb53]" />
                    Specialer og behandlingstyper
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {therapist.specialties.map((item, idx) => (
                      <span
                        key={idx}
                        className="bg-white px-3.5 py-1.5 rounded-full text-sm font-medium text-[#1E352F] border border-[#E6DFD3] shadow-xs"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Uddannelser */}
              {therapist.educations && therapist.educations.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-serif text-xl font-bold text-[#1E352F] flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#eebb53]" />
                    Uddannelser
                  </h3>
                  <ul className="space-y-2.5">
                    {therapist.educations.map((edu, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-[#1E352F]/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1E352F] mt-2 shrink-0" />
                        <span>{edu}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Kurser & efteruddannelse */}
              {therapist.courses && therapist.courses.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-serif text-xl font-bold text-[#1E352F] flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#eebb53]" />
                    Kurser og videreuddannelse
                  </h3>
                  <ul className="space-y-2.5">
                    {therapist.courses.map((course, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-[#1E352F]/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#eebb53] mt-2 shrink-0" />
                        <span>{course}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </div>

          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
            Ønsker du en tid hos {therapist.name}?
          </h2>
          <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Du kan bestille din tid direkte online via Planway eller kontakte os telefonisk på 86 63 93 93, hvis du har spørgsmål til dit behandlingsforløb.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book tid hos {therapist.name}
            </a>
            <Link
              to="/om-os"
              className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-[#F5F0E6] border border-[#F5F0E6]/30 px-8 py-3.5 rounded-full text-base font-semibold transition-colors"
            >
              Mød alle vores behandlere
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
